import React, { useRef, useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  LayoutDashboard, Building2, Users, Home, LogOut, Plus,
  Pencil, Trash2, Image as ImageIcon, StickyNote, X, MessageSquare, Settings2, Contact2, Phone, 
  Mail, MapPin, Clock, Eye, CheckCheck, RefreshCw, Inbox,
} from 'lucide-react';


import { 
  getBuildings, createBuilding, updateBuilding, deleteBuilding, 
  getTeamMembers, createTeamMember, updateTeamMember, deleteTeamMember,
  getContact, updateContact, getMessages, deleteMessage, updateMessage,
  getSetting, saveSetting,
} from '../services/api';

type Tab = 'dashboard'|'buildings'|'team'|'sublets'|'messages'|'contact'|'settings';
type MessageFilter = 'all' | 'unread' | 'read';


interface BuildingImage { url: string; caption: string; file?: File; }
interface Building {
  id?: number; name: string; type: string; location: string; status: string;
  area: string; completedYear: string; description: string; colorIndex: number;
  mainImage?: string;
  images?: BuildingImage[];
  notes?: string[];
}
interface Message  { id?:number; name:string; email:string; subject:string; message:string; phone:string; treated:boolean; createdAt?:string; }
interface Member   { id?:number; name:string; role:string; bio:string; initials:string; colorIndex:number; }
interface Contact  { id?:string; phone?:string; email?:string; address?:string; agency?:string; hours?:string; }
type ModalState = { type:'building'|'team'|'contact'|'message'|null; data:any; mode:'add'|'view'|'edit' };

const BTYPES  = [
  'Road Construction', 'Road Rehabilitation', 'Commercial Building',
  'Office Building', 'Industrial Building', 'Car Park', 'Residential',
  'Lecture Theatre', 'Government Structure'
];
const BSTATUS = ['COMPLETED','AVAILABLE','SUBLET'];
const BCOLORS = [
  'linear-gradient(135deg,#e85d04,#fb8500)',
  'linear-gradient(135deg,#0077b6,#00b4d8)',
  'linear-gradient(135deg,#2d6a4f,#52b788)',
  'linear-gradient(135deg,#6a0572,#a629a6)',
  'linear-gradient(135deg,#1a1a6e,#023e8a)',
  'linear-gradient(135deg,#7b2d00,#c44d00)'
];
const ACCENT_COLORS = [
  { label:'Orange', value:'#e85d04' }, { label:'Blue',   value:'#0077b6' },
  { label:'Green',  value:'#2d6a4f' }, { label:'Purple', value:'#6a0572' },
  { label:'Navy',   value:'#1a1a6e' }, { label:'Red',    value:'#c0392b' },
  { label:'Teal',   value:'#00796b' }, { label:'Gold',   value:'#b8860b' },
];

// ── Helpers used by BOTH admin and visitor pages ──────────────────────────────
export const applyAccentGlobal = (color: string) => {
  document.documentElement.style.setProperty('--brand-orange', color);
  document.documentElement.style.setProperty('--color-primary', color);
};

export const applyThemeGlobal = (mode: 'light'|'dark') => {
  mode === 'dark'
    ? document.documentElement.classList.add('dark')
    : document.documentElement.classList.remove('dark');
};

// ── AdminDashboard ────────────────────────────────────────────────────────────
const AdminDashboard: React.FC = () => {
  const { logout } = useAuth();
  const navigate   = useNavigate();

  const [tab,        setTab]        = useState<Tab>('dashboard');
  const [buildings,  setBuildings]  = useState<Building[]>([]);
  const [team,       setTeam]       = useState<Member[]>([]);
  const [messages,   setMessages]   = useState<Message[]>([]);
  const [msgFilter,  setMsgFilter]  = useState<MessageFilter>('all');
  const [msgLoading, setMsgLoading] = useState(false);
  const [contact,    setContact]    = useState<Contact>({
    phone: '+234 800 NIDAVE', email: 'info@nidave.com',
    address: "La' Goshen Building, Oluyole Industrial Estate, Ibadan",
    hours: 'Mon - Fri: 8am - 5pm', agency: 'sublet@nidave.com',
  });

  // Settings — initialise from CSS/DOM so UI matches whatever is currently applied
  const [accentColor,   setAccentColorState] = useState<string>(() =>
    getComputedStyle(document.documentElement).getPropertyValue('--brand-orange').trim() || '#e85d04');
  const [themeMode,     setThemeModeState]   = useState<'light'|'dark'>(() =>
    document.documentElement.classList.contains('dark') ? 'dark' : 'light');
  const [settingsSaved,    setSettingsSaved]    = useState(false);
  const [settingsSaving,   setSettingsSaving]   = useState(false);
  const [settingsLoading,  setSettingsLoading]  = useState(true);

  const [modal,        setModal]        = useState<ModalState>({ type:null, data:null, mode:'add' });
  const [toast,        setToast]        = useState('');
  const [contactSaving,setContactSaving]= useState(false);

  const token = localStorage.getItem('nidave-token');
  useEffect(() => { if (!token) navigate('/admin/login'); }, [token, navigate]);

  // ── Fetch settings from backend on mount ──────────────────────────────────
  useEffect(() => {
    (async () => {
      setSettingsLoading(true);
      try {
        const r = await getSetting();
        const data = r?.data;
        if (data?.accentColor) {
          setAccentColorState(data.accentColor);
          applyAccentGlobal(data.accentColor);
        }
        if (data?.theme) {
          setThemeModeState(data.theme as 'light'|'dark');
          applyThemeGlobal(data.theme as 'light'|'dark');
        }
      } catch {
        // fallback: silently keep DOM defaults
      } finally {
        setSettingsLoading(false);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Fetch messages ─────────────────────────────────────────────────────────
  const fetchMessages = async () => {
    setMsgLoading(true);
    try { const r = await getMessages(); if (r.data) setMessages(r.data); }
    catch {}
    finally { setMsgLoading(false); }
  };
  useEffect(() => { fetchMessages(); }, []);

  // ── Fetch buildings, team, contact ─────────────────────────────────────────
  useEffect(() => {
    getBuildings().then(r  => { if (r.data?.length)  setBuildings(r.data);  }).catch(() => {});
    getTeamMembers().then(r => { if (r.data?.length)  setTeam(r.data);       }).catch(() => {});
    getContact().then(r    => { if (r.data)           setContact(r.data);    }).catch(() => {});
  }, []);

  const showToast  = (msg: string) => { setToast(msg); setTimeout(() => setToast(''), 3000); };
  const openModal  = (type: 'building'|'team'|'contact'|'message', data: any, mode: 'view'|'add'|'edit') =>
    setModal({ type, data: { ...data }, mode });
  const closeModal = () => setModal({ type:null, data:null, mode:'add' });

  // ── Buildings ──────────────────────────────────────────────────────────────
  const saveBuilding = async (d: Building, m: File, e: { file: File; caption: string }[]) => {
    try { 
      const formData = new FormData();

      Object.entries(d).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach((v, i) => {
            formData.append(`${key}[${i}]`, v);
          });
        } else if (value !== undefined && value !== null) {
          formData.append(key, value.toString());
        }
      });

      // main image
      if (m instanceof File) {
        formData.append("mainImage", m);
      }

      // extra images
      e.forEach((item, i) => {
        if (item.file instanceof File) {
          formData.append("images", item.file);
        }

        if (item.caption) {
          formData.append("notes", item.caption);
        }
      });

      modal.mode === 'add' ? 
        await createBuilding(formData) : await updateBuilding(d.id!, formData); 
    } catch (error) {
      showToast(error.response?.data?.message || 
        modal.mode === 'add' ?
         'Unable to add a new Building!' : 'Unable to update Building!'
      );

      return;
    }

    modal.mode === 'add'
      ? setBuildings(p => [...p, { ...d, id: Date.now() }])
      : setBuildings(p => p.map(b => b.id === d.id ? d : b));

    showToast(modal.mode === 'add' ? 'Building added!' : 'Building updated!');
    closeModal();
  };

  const delBuilding = async (id: number) => {
    if (!window.confirm('Delete this building?')) return;
    try { await deleteBuilding(id); } catch {}
    setBuildings(p => p.filter(b => b.id !== id));
    showToast('Building deleted.');
  };

  // ── Team ───────────────────────────────────────────────────────────────────
  const saveMember = async (d: Member) => {
    try { modal.mode === 'add' ? await createTeamMember(d) : await updateTeamMember(d.id!, d); } catch {}
    modal.mode === 'add'
      ? setTeam(p => [...p, { ...d, id: Date.now() }])
      : setTeam(p => p.map(m => m.id === d.id ? d : m));
    showToast(modal.mode === 'add' ? 'Member added!' : 'Member updated!');
    closeModal();
  };
  const delMember = async (id: number) => {
    if (!window.confirm('Delete this member?')) return;
    try { await deleteTeamMember(id); } catch {}
    setTeam(p => p.filter(m => m.id !== id));
    showToast('Member deleted.');
  };

  // ── Contact ────────────────────────────────────────────────────────────────
  const submitContact = async (updated: Contact) => {
    if (!updated.phone || !updated.email || !updated.address || !updated.agency || !updated.hours) {
      showToast('Please fill in all contact fields.'); return;
    }
    setContactSaving(true);
    try   { await updateContact(updated); setContact(updated); showToast('Contact info updated!'); }
    catch { showToast('Failed to update contact. Please try again.'); }
    finally { setContactSaving(false); closeModal(); }
  };

  // ── Messages ───────────────────────────────────────────────────────────────
  const markTreated = async (id: number, treated: boolean) => {
    try {
      await updateMessage(id);
    } catch (err: any) {
      showToast(err?.response?.data?.message || 'Unable to update message.');
      return;
    }
    setMessages(p => p.map(m => m.id === id ? { ...m, treated } : m));
    setModal(prev => prev.type === 'message' && prev.data?.id === id
      ? { ...prev, data: { ...prev.data, treated } } : prev);
    showToast(treated ? 'Marked as treated.' : 'Marked as unread.');
  };

  const delMessage = async (id: number) => {
    if (!window.confirm('Delete this message? This cannot be undone.')) return;
    try {
      await deleteMessage(id);
    } catch (err: any) {
      showToast(err?.response?.data?.message || 'Unable to delete message.');
      return;
    }
    setMessages(p => p.filter(m => m.id !== id));
    if (modal.type === 'message' && modal.data?.id === id) closeModal();
    showToast('Message deleted.');
  };

  const filteredMessages = messages.filter(m => {
    if (msgFilter === 'read')   return  m.treated;
    if (msgFilter === 'unread') return !m.treated;
    return true;
  });
  const unreadCount = messages.filter(m => !m.treated).length;

  // ── Site Settings ──────────────────────────────────────────────────────────
  // These helpers update state + DOM immediately (live preview)
  const applyAccent = (color: string) => {
    setAccentColorState(color);
    applyAccentGlobal(color);
  };
  const applyTheme = (mode: 'light'|'dark') => {
    setThemeModeState(mode);
    applyThemeGlobal(mode);
  };

  // Persist to backend
  const saveSettings = async () => {
    setSettingsSaving(true);
    try {
      await saveSetting({ accentColor, theme: themeMode });
      setSettingsSaved(true);
      showToast('Settings saved!');
      setTimeout(() => setSettingsSaved(false), 3000);
    } catch (err: any) {
      showToast(err?.response?.data?.message || 'Failed to save settings.');
    } finally {
      setSettingsSaving(false);
    }
  };

  // ── Nav ────────────────────────────────────────────────────────────────────
  const navItems = [
    { id:'dashboard', label:'Dashboard',       icon:<LayoutDashboard size={17}/> },
    { id:'buildings', label:'Buildings',       icon:<Building2 size={17}/> },
    { id:'team',      label:'Team Members',    icon:<Users size={17}/> },
    { id:'sublets',   label:'Sublet Listings', icon:<Home size={17}/> },
    { id:'contact',   label:'Contact Us',      icon:<Contact2 size={17}/> },
    { id:'messages',  label:'Messages',        icon:<MessageSquare size={17}/>, badge: unreadCount },
    { id:'settings',  label:'Site Settings',   icon:<Settings2 size={17}/> },
  ];

  if (!token) return null;

  return (
    <div className="admin-layout">

      {/* ── Sidebar ── */}
      <aside className="admin-sidebar">
        <div className="admin-sidebar-logo">
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <div className="w-10 h-10 bg-gradient-to-br from-primary-600 to-primary-800 rounded-lg flex items-center justify-center">
              <img src="/nidaveLogo.png" alt="Nidave Logo" className="w-10 h-10" />
            </div>
            <div>
              <span className="nav-logo-name" style={{ fontSize:'1.05rem' }}>NIDAVE</span>
              <span className="nav-logo-tagline">Admin CMS</span>
            </div>
          </div>
        </div>
        <nav style={{ flex:1 }}>
          {navItems.map(item => (
            <button key={item.id}
              className={`admin-nav-item${tab === item.id ? ' active' : ''}`}
              onClick={() => setTab(item.id as Tab)}
              style={{ position:'relative' }}>
              {item.icon}{item.label}
              {(item as any).badge > 0 && (
                <span style={{
                  marginLeft:'auto', minWidth:20, height:20, borderRadius:10,
                  background:'var(--brand-orange)', color:'#fff',
                  fontSize:'0.7rem', fontWeight:700,
                  display:'flex', alignItems:'center', justifyContent:'center', padding:'0 5px',
                }}>{(item as any).badge}</span>
              )}
            </button>
          ))}
        </nav>
        <div style={{ marginTop:'auto', borderTop:'1px solid var(--border)', paddingTop:8 }}>
          <Link to="/" className="admin-nav-item" style={{ display:'flex' }}>
            <MessageSquare size={17}/>View Website
          </Link>
          <button className="admin-nav-item" onClick={() => { logout(); navigate('/'); }} style={{ color:'#dc3545' }}>
            <LogOut size={17}/>Logout
          </button>
        </div>
      </aside>

      {/* ── Main ── */}
      <main className="admin-main">

        {/* ── Dashboard ── */}
        {tab === 'dashboard' && (
          <div>
            <div className="admin-header">
              <div>
                <h1 className="admin-title">Dashboard</h1>
                <p style={{ color:'var(--text-muted)', fontSize:'0.88rem' }}>Welcome back, Admin</p>
              </div>
            </div>
            <div className="admin-stats-grid">
              {[
                { n: buildings.length,                                                         l:'Total Buildings'       },
                { n: buildings.filter(b => b.status==='AVAILABLE'||b.status==='SUBLET').length,l:'Available for Sublet'  },
                { n: team.length,                                                              l:'Team Members'          },
                { n: buildings.filter(b => b.status==='COMPLETED').length,                    l:'Completed Projects'    },
              ].map(s => (
                <div className="admin-stat-card" key={s.l}>
                  <div className="admin-stat-num">{s.n}</div>
                  <div className="admin-stat-label">{s.l}</div>
                </div>
              ))}
            </div>
            <div className="admin-card">
              <h3 style={{ fontFamily:'var(--font-condensed)', fontSize:'1.05rem', fontWeight:700, letterSpacing:'0.04em', color:'var(--text-primary)', marginBottom:18 }}>Quick Actions</h3>
              <div style={{ display:'flex', gap:12, flexWrap:'wrap' }}>
                <button className="btn-primary" onClick={() => { setTab('buildings'); openModal('building', { name:'',type:'',location:'',status:'COMPLETED',area:'',completedYear:'2024',description:'',colorIndex:0 }, 'add'); }}>
                  <Plus size={15}/>Add Building
                </button>
                <button className="btn-outline" onClick={() => { setTab('team'); openModal('team', { name:'',role:'',bio:'',initials:'',colorIndex:0 }, 'add'); }}>
                  <Plus size={15}/>Add Team Member
                </button>
              </div>
            </div>
            <div className="admin-card" style={{ padding:0 }}>
              <div style={{ padding:'20px 24px', borderBottom:'1px solid var(--border)' }}>
                <h3 style={{ fontFamily:'var(--font-condensed)', fontSize:'1.05rem', fontWeight:700, color:'var(--text-primary)' }}>Recent Buildings</h3>
              </div>
              <table className="admin-table">
                <thead><tr><th>Name</th><th>Type</th><th>Status</th><th>Year</th></tr></thead>
                <tbody>
                  {buildings.slice(0,5).map(b => (
                    <tr key={b.id}>
                      <td style={{ fontWeight:600, color:'var(--text-primary)' }}>{b.name}</td>
                      <td>{b.type}</td>
                      <td><span className={`building-status status-${(b.status||'').toLowerCase()}`} style={{ position:'static' }}>{b.status}</span></td>
                      <td>{b.completedYear}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── Buildings ── */}
        {tab === 'buildings' && (
          <div>
            <div className="admin-header">
              <h1 className="admin-title">Buildings</h1>
              <button className="btn-primary" onClick={() => openModal('building', { name:'',type:'',location:'',status:'COMPLETED',area:'',completedYear:'2024',description:'',colorIndex:0 }, 'add')}>
                <Plus size={15}/>Add Building
              </button>
            </div>
            <div className="admin-card" style={{ padding:0 }}>
              <table className="admin-table">
                <thead><tr><th>Name</th><th>Type</th><th>Location</th><th>Status</th><th>Area</th><th>Year</th><th>Actions</th></tr></thead>
                <tbody>
                  {buildings.map(b => (
                    <tr key={b.id}>
                      <td style={{ fontWeight:600, color:'var(--text-primary)' }}>{b.name}</td>
                      <td>{b.type}</td><td>{b.location}</td>
                      <td><span className={`building-status status-${(b.status||'').toLowerCase()}`} style={{ position:'static' }}>{b.status}</span></td>
                      <td>{b.area}</td><td>{b.completedYear}</td>
                      <td>
                        <div style={{ display:'flex', gap:6 }}>
                          <button className="action-btn action-btn-edit" onClick={() => openModal('building', b, 'edit')}><Pencil size={13}/></button>
                          <button className="action-btn action-btn-delete" onClick={() => delBuilding(b.id!)}><Trash2 size={13}/></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── Team ── */}
        {tab === 'team' && (
          <div>
            <div className="admin-header">
              <h1 className="admin-title">Team Members</h1>
              <button className="btn-primary" onClick={() => openModal('team', { name:'',role:'',bio:'',initials:'',colorIndex:0 }, 'add')}>
                <Plus size={15}/>Add Member
              </button>
            </div>
            <div className="admin-card" style={{ padding:0 }}>
              <table className="admin-table">
                <thead><tr><th>Name</th><th>Role</th><th>Initials</th><th>Bio</th><th>Actions</th></tr></thead>
                <tbody>
                  {team.map(m => (
                    <tr key={m.id}>
                      <td style={{ fontWeight:600, color:'var(--text-primary)' }}>{m.name}</td>
                      <td>{m.role}</td>
                      <td><span style={{ fontFamily:'var(--font-display)', color:'var(--brand-orange)', fontSize:'1.1rem' }}>{m.initials}</span></td>
                      <td style={{ maxWidth:200, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{m.bio}</td>
                      <td>
                        <div style={{ display:'flex', gap:6 }}>
                          <button className="action-btn action-btn-edit" onClick={() => openModal('team', m, 'edit')}><Pencil size={13}/></button>
                          <button className="action-btn action-btn-delete" onClick={() => delMember(m.id!)}><Trash2 size={13}/></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── Sublets ── */}
        {tab === 'sublets' && (
          <div>
            <div className="admin-header"><h1 className="admin-title">Sublet Listings</h1></div>
            <div className="admin-card">
              <p style={{ color:'var(--text-secondary)', marginBottom:20 }}>
                Sublet listings come from buildings with <strong>AVAILABLE</strong> or <strong>SUBLET</strong> status.
              </p>
              <table className="admin-table">
                <thead><tr><th>Building</th><th>Location</th><th>Status</th><th>Area</th></tr></thead>
                <tbody>
                  {buildings.filter(b => b.status==='AVAILABLE'||b.status==='SUBLET').map(b => (
                    <tr key={b.id}>
                      <td style={{ fontWeight:600, color:'var(--text-primary)' }}>{b.name}</td>
                      <td>{b.location}</td>
                      <td><span className={`building-status status-${(b.status||'').toLowerCase()}`} style={{ position:'static' }}>{b.status}</span></td>
                      <td>{b.area}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── Contact Us ── */}
        {tab === 'contact' && (
          <div>
            <div className="admin-header">
              <div>
                <h1 className="admin-title">Contact Information</h1>
                <p style={{ color:'var(--text-muted)', fontSize:'0.88rem' }}>Manage the company contact details shown on the website</p>
              </div>
              <button className="btn-primary" onClick={() => openModal('contact', contact, 'edit')}>
                <Pencil size={15}/> Edit Contact Info
              </button>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(280px, 1fr))', gap:16, marginBottom:24 }}>
              {[
                { icon:<Phone size={20}/>,  label:'Phone Number',   value: contact.phone   },
                { icon:<Mail size={20}/>,   label:'Official Email', value: contact.email   },
                { icon:<Mail size={20}/>,   label:'Agency Email',   value: contact.agency  },
                { icon:<MapPin size={20}/>, label:'Address',        value: contact.address },
                { icon:<Clock size={20}/>,  label:'Working Hours',  value: contact.hours   },
              ].map(item => (
                <div key={item.label} className="admin-card" style={{ display:'flex', alignItems:'flex-start', gap:14, padding:'18px 20px' }}>
                  <div style={{ width:40, height:40, borderRadius:10, flexShrink:0, background:'var(--brand-orange)', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', opacity:0.9 }}>
                    {item.icon}
                  </div>
                  <div style={{ minWidth:0 }}>
                    <div style={{ fontSize:'0.75rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', color:'var(--text-muted)', marginBottom:4 }}>{item.label}</div>
                    <div style={{ fontSize:'0.92rem', color:'var(--text-primary)', fontWeight:500, wordBreak:'break-word' }}>
                      {item.value || <span style={{ color:'var(--text-muted)', fontStyle:'italic' }}>Not set</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Messages ── */}
        {tab === 'messages' && (
          <div>
            <div className="admin-header">
              <div>
                <h1 className="admin-title">Messages</h1>
                <p style={{ color:'var(--text-muted)', fontSize:'0.88rem' }}>
                  {unreadCount > 0 ? `${unreadCount} unread message${unreadCount !== 1 ? 's' : ''}` : 'All messages treated'}
                </p>
              </div>
              <button className="btn-outline" onClick={fetchMessages} disabled={msgLoading}
                style={{ display:'flex', alignItems:'center', gap:6 }}>
                <RefreshCw size={14} style={{ animation: msgLoading ? 'spin 1s linear infinite' : 'none' }}/>
                Refresh
              </button>
            </div>

            {/* Filter pills */}
            <div style={{ display:'flex', gap:8, marginBottom:20 }}>
              {([
                { key:'all',    label:'All',     count: messages.length },
                { key:'unread', label:'Unread',  count: messages.filter(m => !m.treated).length },
                { key:'read',   label:'Treated', count: messages.filter(m =>  m.treated).length },
              ] as { key: MessageFilter; label: string; count: number }[]).map(f => (
                <button key={f.key} onClick={() => setMsgFilter(f.key)} style={{
                  display:'flex', alignItems:'center', gap:7,
                  padding:'7px 16px', borderRadius:20, cursor:'pointer',
                  fontSize:'0.83rem', fontWeight:600,
                  border:      msgFilter===f.key ? '2px solid var(--brand-orange)' : '2px solid var(--border)',
                  background:  msgFilter===f.key ? 'var(--brand-orange)' : 'var(--surface)',
                  color:       msgFilter===f.key ? '#fff' : 'var(--text-secondary)',
                  transition: 'all 0.15s',
                }}>
                  {f.label}
                  <span style={{
                    minWidth:20, height:20, borderRadius:10, padding:'0 5px',
                    background: msgFilter===f.key ? 'rgba(255,255,255,0.25)' : 'var(--border)',
                    color:      msgFilter===f.key ? '#fff' : 'var(--text-muted)',
                    fontSize:'0.72rem', fontWeight:700,
                    display:'flex', alignItems:'center', justifyContent:'center',
                  }}>{f.count}</span>
                </button>
              ))}
            </div>

            <div className="admin-card" style={{ padding:0 }}>
              {msgLoading ? (
                <div style={{ padding:40, textAlign:'center', color:'var(--text-muted)' }}>Loading messages…</div>
              ) : filteredMessages.length === 0 ? (
                <div style={{ padding:48, textAlign:'center' }}>
                  <Inbox size={40} style={{ color:'var(--text-muted)', marginBottom:12, opacity:0.5 }}/>
                  <p style={{ color:'var(--text-muted)', fontSize:'0.9rem' }}>
                    No {msgFilter !== 'all' ? msgFilter : ''} messages found
                  </p>
                </div>
              ) : (
                <table className="admin-table">
                  <thead><tr><th>Status</th><th>Name</th><th>Email</th><th>Subject</th><th>Phone</th><th>Actions</th></tr></thead>
                  <tbody>
                    {filteredMessages.map(m => (
                      <tr key={m.id} style={{ opacity: m.treated ? 0.65 : 1, cursor:'pointer' }}
                        onClick={() => openModal('message', m, 'view')}>
                        <td onClick={e => e.stopPropagation()}>
                          <span style={{
                            display:'inline-flex', alignItems:'center', gap:5,
                            padding:'3px 10px', borderRadius:20, fontSize:'0.73rem', fontWeight:700,
                            background: m.treated ? 'rgba(45,106,79,0.12)' : 'rgba(232,93,4,0.1)',
                            color:      m.treated ? '#2d6a4f' : 'var(--brand-orange)',
                          }}>
                            <span style={{ width:6, height:6, borderRadius:'50%', background:'currentColor', display:'inline-block' }}/>
                            {m.treated ? 'Treated' : 'Unread'}
                          </span>
                        </td>
                        <td style={{ fontWeight: m.treated ? 400 : 700, color:'var(--text-primary)' }}>{m.name}</td>
                        <td>{m.email}</td>
                        <td style={{ maxWidth:180, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{m.subject || '—'}</td>
                        <td>{m.phone || '—'}</td>
                        <td onClick={e => e.stopPropagation()}>
                          <div style={{ display:'flex', gap:6 }}>
                            <button className="action-btn action-btn-edit" title="View"
                              onClick={() => openModal('message', m, 'view')}><Eye size={13}/></button>
                            {!m.treated ? (
                              <button title="Mark as treated" onClick={() => markTreated(m.id!, true)}
                                style={{ background:'rgba(45,106,79,0.1)', color:'#2d6a4f', border:'1px solid rgba(45,106,79,0.2)', borderRadius:6, padding:'5px 7px', cursor:'pointer' }}>
                                <CheckCheck size={13}/>
                              </button>
                            ) : (
                              <button title="Mark as unread" onClick={() => markTreated(m.id!, false)}
                                style={{ background:'rgba(232,93,4,0.08)', color:'var(--brand-orange)', border:'1px solid rgba(232,93,4,0.2)', borderRadius:6, padding:'5px 7px', cursor:'pointer' }}>
                                <RefreshCw size={13}/>
                              </button>
                            )}
                            <button className="action-btn action-btn-delete" title="Delete"
                              onClick={() => delMessage(m.id!)}><Trash2 size={13}/></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {/* ── Site Settings ── */}
        {tab === 'settings' && (
          <div>
            <div className="admin-header">
              <div>
                <h1 className="admin-title">Site Settings</h1>
                <p style={{ color:'var(--text-muted)', fontSize:'0.88rem' }}>
                  Changes apply site-wide for all visitors once saved
                </p>
              </div>
            </div>

            {settingsLoading ? (
              <div style={{ padding:40, textAlign:'center', color:'var(--text-muted)' }}>Loading settings…</div>
            ) : (
              <>
                {/* Accent Color */}
                <div className="admin-card" style={{ marginBottom:16 }}>
                  <h3 style={{ fontFamily:'var(--font-condensed)', fontSize:'1rem', fontWeight:700, letterSpacing:'0.04em', color:'var(--text-primary)', marginBottom:6 }}>Accent Color</h3>
                  <p style={{ color:'var(--text-muted)', fontSize:'0.83rem', marginBottom:18 }}>
                    The primary brand color used across buttons, highlights, and icons on the public website.
                    Visitors cannot change this.
                  </p>
                  <div style={{ display:'flex', flexWrap:'wrap', gap:12 }}>
                    {ACCENT_COLORS.map(c => (
                      <button key={c.value} title={c.label} aria-label={c.label}
                        onClick={() => applyAccent(c.value)}
                        style={{
                          width:44, height:44, borderRadius:12, background:c.value, cursor:'pointer',
                          border:      accentColor===c.value ? '3px solid var(--text-primary)' : '3px solid transparent',
                          boxShadow:   accentColor===c.value ? '0 0 0 2px var(--brand-orange)' : 'none',
                          transition: 'transform 0.15s',
                          transform:   accentColor===c.value ? 'scale(1.15)' : 'scale(1)',
                        }}/>
                    ))}
                  </div>
                  {/* Custom hex */}
                  <div style={{ display:'flex', alignItems:'center', gap:10, marginTop:18 }}>
                    <label style={{ fontSize:'0.83rem', color:'var(--text-secondary)', fontWeight:600, whiteSpace:'nowrap' }}>Custom hex</label>
                    <input type="color" value={accentColor} onChange={e => applyAccent(e.target.value)}
                      style={{ width:40, height:34, borderRadius:8, border:'1px solid var(--border)', cursor:'pointer', padding:2 }}/>
                    <input type="text" value={accentColor} className="form-input" placeholder="#e85d04" style={{ width:100 }}
                      onChange={e => { if (/^#[0-9a-fA-F]{0,6}$/.test(e.target.value)) applyAccent(e.target.value); }}/>
                    <div style={{ width:34, height:34, borderRadius:8, background:accentColor, border:'1px solid var(--border)' }}/>
                  </div>
                </div>

                {/* Default Theme */}
                <div className="admin-card" style={{ marginBottom:24 }}>
                  <h3 style={{ fontFamily:'var(--font-condensed)', fontSize:'1rem', fontWeight:700, letterSpacing:'0.04em', color:'var(--text-primary)', marginBottom:6 }}>Default Theme</h3>
                  <p style={{ color:'var(--text-muted)', fontSize:'0.83rem', marginBottom:18 }}>
                    Sets the default theme for visitors when they first visit the site.
                    Visitors can switch between light and dark on their own.
                  </p>
                  <div style={{ display:'flex', gap:12 }}>
                    {(['light', 'dark'] as const).map(mode => (
                      <button key={mode} onClick={() => applyTheme(mode)} style={{
                        display:'flex', alignItems:'center', gap:10,
                        padding:'12px 22px', borderRadius:12, cursor:'pointer',
                        fontWeight:600, fontSize:'0.88rem', textTransform:'capitalize',
                        border:     themeMode===mode ? '2px solid var(--brand-orange)' : '2px solid var(--border)',
                        background: themeMode===mode ? 'var(--brand-orange)' : 'var(--surface)',
                        color:      themeMode===mode ? '#fff' : 'var(--text-primary)',
                        transition: 'all 0.2s',
                      }}>
                        <span style={{ fontSize:'1.2rem' }}>{mode === 'light' ? '☀️' : '🌙'}</span>
                        {mode} Mode
                      </button>
                    ))}
                  </div>
                </div>

                {/* Save */}
                <div style={{ display:'flex', alignItems:'center', gap:14 }}>
                  <button className="btn-primary" onClick={saveSettings} disabled={settingsSaving}
                    style={{ padding:'10px 28px', opacity: settingsSaving ? 0.7 : 1 }}>
                    {settingsSaving ? 'Saving…' : 'Save Settings'}
                  </button>
                  {settingsSaved && (
                    <span style={{ color:'#2d6a4f', fontSize:'0.88rem', fontWeight:600 }}>✓ Saved successfully</span>
                  )}
                </div>
              </>
            )}
          </div>
        )}

      </main>

      {/* ── Building Modal ── */}
      {modal.type === 'building' && modal.data && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{modal.mode === 'add' ? 'Add Building' : 'Edit Building'}</h3>
              <button className="modal-close" onClick={closeModal}><X size={16}/></button>
            </div>
            <BuildingForm data={modal.data} mode={modal.mode} onSave={saveBuilding} onClose={closeModal}/>
          </div>
        </div>
      )}

      {/* ── Team Modal ── */}
      {modal.type === 'team' && modal.data && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{modal.mode === 'add' ? 'Add Team Member' : 'Edit Team Member'}</h3>
              <button className="modal-close" onClick={closeModal}><X size={16}/></button>
            </div>
            <TeamForm data={modal.data} mode={modal.mode} onSave={saveMember} onClose={closeModal}/>
          </div>
        </div>
      )}

      {/* ── Contact Modal ── */}
      {modal.type === 'contact' && modal.data && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth:560 }}>
            <div className="modal-header">
              <h3 className="modal-title">Edit Contact Information</h3>
              <button className="modal-close" onClick={closeModal}><X size={16}/></button>
            </div>
            <ContactForm data={modal.data} onSave={submitContact} onClose={closeModal} saving={contactSaving}/>
          </div>
        </div>
      )}

      {/* ── Message View Modal ── */}
      {modal.type === 'message' && modal.data && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth:580 }}>
            <div className="modal-header">
              <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                <h3 className="modal-title">Message</h3>
                <span style={{
                  padding:'3px 10px', borderRadius:20, fontSize:'0.72rem', fontWeight:700,
                  background: modal.data.treated ? 'rgba(45,106,79,0.12)' : 'rgba(232,93,4,0.1)',
                  color:      modal.data.treated ? '#2d6a4f' : 'var(--brand-orange)',
                }}>
                  {modal.data.treated ? 'Treated' : 'Unread'}
                </span>
              </div>
              <button className="modal-close" onClick={closeModal}><X size={16}/></button>
            </div>
            <div style={{ padding:'0 24px 24px' }}>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:20 }}>
                {[
                  { label:'From',    value: modal.data.name           },
                  { label:'Email',   value: modal.data.email          },
                  { label:'Phone',   value: modal.data.phone  || '—'  },
                  { label:'Subject', value: modal.data.subject || '—' },
                ].map(row => (
                  <div key={row.label} style={{ background:'var(--surface)', borderRadius:8, padding:'10px 14px', border:'1px solid var(--border)' }}>
                    <div style={{ fontSize:'0.7rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', color:'var(--text-muted)', marginBottom:3 }}>{row.label}</div>
                    <div style={{ fontSize:'0.88rem', color:'var(--text-primary)', fontWeight:500, wordBreak:'break-word' }}>{row.value}</div>
                  </div>
                ))}
              </div>
              <div style={{ marginBottom:24 }}>
                <div style={{ fontSize:'0.7rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', color:'var(--text-muted)', marginBottom:8 }}>Message</div>
                <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:10, padding:'16px 18px', fontSize:'0.9rem', color:'var(--text-primary)', lineHeight:1.75, whiteSpace:'pre-wrap', minHeight:100 }}>
                  {modal.data.message}
                </div>
              </div>
              <div style={{ display:'flex', gap:10, flexWrap:'wrap', alignItems:'center' }}>
                {!modal.data.treated ? (
                  <button className="btn-primary" style={{ display:'flex', alignItems:'center', gap:7 }}
                    onClick={() => markTreated(modal.data.id, true)}>
                    <CheckCheck size={15}/> Mark as Treated
                  </button>
                ) : (
                  <button className="btn-outline" style={{ display:'flex', alignItems:'center', gap:7 }}
                    onClick={() => markTreated(modal.data.id, false)}>
                    <RefreshCw size={15}/> Mark as Unread
                  </button>
                )}
                <a href={`mailto:${modal.data.email}?subject=Re: ${encodeURIComponent(modal.data.subject || 'Your enquiry')}`}
                  className="btn-outline" style={{ display:'flex', alignItems:'center', gap:7, textDecoration:'none' }}>
                  <Mail size={15}/> Reply via Email
                </a>
                <button onClick={() => delMessage(modal.data.id)}
                  style={{ display:'flex', alignItems:'center', gap:7, marginLeft:'auto', padding:'8px 14px', borderRadius:8, border:'1px solid rgba(220,53,69,0.3)', background:'rgba(220,53,69,0.06)', color:'#dc3545', cursor:'pointer', fontSize:'0.83rem', fontWeight:600 }}>
                  <Trash2 size={14}/> Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className="toast-container">
          <div className="toast toast-success">{toast}</div>
        </div>
      )}
    </div>
  );
};

// ── Shared upload button style ─────────────────────────────────────────────────
const uploadBtnStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  gap: 6, padding: '9px 16px', borderRadius: 8, cursor: 'pointer',
  fontSize: '0.82rem', fontWeight: 600,
  border: '1.5px dashed var(--border, #ddd)',
  background: 'var(--surface-alt, #f8f8f8)', color: 'var(--text-secondary, #555)',
};
  
// ── BuildingForm ───────────────────────────────────────────────────────────────
const BuildingForm: React.FC<{
  data: Building;
  mode: string;
  onSave: (d: Building, mainFile?: File, extraFiles?: { file: File; caption: string }[]) => void;
  onClose: () => void;
}> = ({ data, mode, onSave, onClose }) => {
  const [form, setForm] = useState<Building>({
    ...data,
    // Normalise images: backend may return { url } or { url, caption } or just a string
    images: data.images
      ? data.images.map(img =>
          typeof img === 'string'
            ? { url: img, caption: '' }
            : { url: img.url, caption: img.caption ?? '' }
        )
      : [],
    notes: data.notes ?? [],
  });

  // New file chosen for main image (not yet on server)
  const [mainFile,    setMainFile]    = useState<File | null>(null);
  // Preview URL: initialised from the existing saved URL, replaced when user picks a file
  const [mainPreview, setMainPreview] = useState<string>(data.mainImage ?? '');

  // Pending extra files (not yet on server)
  const [extraFiles, setExtraFiles] = useState<{ file: File; caption: string; preview: string }[]>([]);

  const mainInputRef  = useRef<HTMLInputElement>(null);
  const extraInputRef = useRef<HTMLInputElement>(null);

  const ch = (e: any) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  // ── Main image ──────────────────────────────────────────────────────────────
  const onMainFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setMainFile(file);
    setMainPreview(URL.createObjectURL(file));
    setForm(f => ({ ...f, mainImage: undefined })); // clear persisted URL so backend knows to use the new file
  };

  const removeMainImage = () => {
    setMainFile(null);
    setMainPreview('');
    setForm(f => ({ ...f, mainImage: undefined }));
    if (mainInputRef.current) mainInputRef.current.value = '';
  };

  // ── Persisted images (already on server) ────────────────────────────────────
  const updatePersistedCaption = (i: number, caption: string) =>
    setForm(f => ({
      ...f,
      images: f.images?.map((img, idx) => idx === i ? { ...img, caption } : img),
    }));

  const removePersistedImage = (i: number) =>
    setForm(f => ({ ...f, images: f.images?.filter((_, idx) => idx !== i) }));

  // ── New extra files ──────────────────────────────────────────────────────────
  const onExtraFilesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    setExtraFiles(p => [
      ...p,
      ...files.map(file => ({ file, caption: '', preview: URL.createObjectURL(file) })),
    ]);
    if (extraInputRef.current) extraInputRef.current.value = '';
  };
  const updateExtraCaption = (i: number, caption: string) =>
    setExtraFiles(p => p.map((x, idx) => idx === i ? { ...x, caption } : x));
  const removeExtra = (i: number) =>
    setExtraFiles(p => p.filter((_, idx) => idx !== i));

  // ── Notes ────────────────────────────────────────────────────────────────────
  const addNote    = () => setForm(f => ({ ...f, notes: [...(f.notes ?? []), ''] }));
  const updateNote = (i: number, val: string) =>
    setForm(f => ({ ...f, notes: f.notes?.map((n, idx) => idx === i ? val : n) }));
  const removeNote = (i: number) =>
    setForm(f => ({ ...f, notes: f.notes?.filter((_, idx) => idx !== i) }));

  // ── Submit ────────────────────────────────────────────────────────────────────
  const handleSave = () =>
    onSave(form, mainFile ?? undefined, extraFiles.map(({ file, caption }) => ({ file, caption })));

  return (
    <div className="contact-form" style={{ maxHeight:'75vh', overflowY:'auto', paddingRight:4 }}>

      {/* ── Basic fields ── */}
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Name *</label>
          <input className="form-input" name="name" value={form.name} onChange={ch} placeholder="Building name"/>
        </div>
        <div className="form-group">
          <label className="form-label">Type</label>
          <select className="form-select" name="type" value={form.type} onChange={ch}>
            <option value="">Select type</option>
            {BTYPES.map(t => <option key={t}>{t}</option>)}
          </select>
        </div>
      </div>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Location</label>
          <input className="form-input" name="location" value={form.location} onChange={ch} placeholder="City, State"/>
        </div>
        <div className="form-group">
          <label className="form-label">Status</label>
          <select className="form-select" name="status" value={form.status} onChange={ch}>
            {BSTATUS.map(s => <option key={s}>{s}</option>)}
          </select>
        </div>
      </div>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Area / Size</label>
          <input className="form-input" name="area" value={form.area} onChange={ch} placeholder="e.g. 3,200 sqm"/>
        </div>
        <div className="form-group">
          <label className="form-label">Year</label>
          <input className="form-input" name="completedYear" value={form.completedYear} onChange={ch} placeholder="2024"/>
        </div>
      </div>
      <div className="form-group">
        <label className="form-label">Description</label>
        <textarea className="form-textarea" name="description" value={form.description} onChange={ch}
          placeholder="Brief description..." style={{ minHeight:72 }}/>
      </div>

      {/* ── Card color ── */}
      <div style={{ fontSize:'0.78rem', fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', color:'var(--text-muted)', marginBottom:8 }}>
        Card Color
      </div>
      <div style={{ display:'flex', gap:8, marginBottom:20 }}>
        {BCOLORS.map((c, i) => (
          <div key={i} onClick={() => setForm(f => ({ ...f, colorIndex: i }))}
            style={{ width:28, height:28, borderRadius:'50%', background:c, cursor:'pointer',
              border: form.colorIndex === i ? '3px solid var(--text-primary)' : '3px solid transparent' }}/>
        ))}
      </div>

      <div style={{ borderTop:'1px solid var(--border)', margin:'4px 0 20px' }}/>

      {/* ══ MAIN / COVER IMAGE ══════════════════════════════════════════════════ */}
      <div style={{ marginBottom:20 }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
          <div style={{ display:'flex', alignItems:'center', gap:6 }}>
            <ImageIcon size={14} color="var(--brand-orange,#e85d04)"/>
            <span style={{ fontSize:'0.78rem', fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', color:'var(--text-muted)' }}>
              Main / Cover Image
            </span>
          </div>
          {/* Replace button shown alongside the preview */}
          {mainPreview && (
            <label style={{ ...uploadBtnStyle, padding:'5px 12px', cursor:'pointer', fontSize:'0.78rem' }}>
              <ImageIcon size={13}/> Replace
              <input ref={mainInputRef} type="file" accept="image/*" style={{ display:'none' }} onChange={onMainFileChange}/>
            </label>
          )}
        </div>

        {mainPreview ? (
          <div style={{ position:'relative', width:'100%', height:200, borderRadius:10, overflow:'hidden', border:'1px solid var(--border)' }}>
            <img
              src={mainPreview}
              alt="Cover"
              style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }}
              onError={e => { (e.target as HTMLImageElement).style.opacity = '0.2'; }}
            />
            {/* Status tag */}
            <span style={{
              position:'absolute', bottom:8, left:8,
              background: mainFile ? 'rgba(232,93,4,0.85)' : 'rgba(45,106,79,0.85)',
              color:'#fff', fontSize:'0.68rem', fontWeight:700,
              padding:'3px 9px', borderRadius:8,
            }}>
              {mainFile ? '● New — not saved yet' : '✓ Saved cover image'}
            </span>
            <button onClick={removeMainImage} style={{
              position:'absolute', top:8, right:8, width:28, height:28, borderRadius:'50%',
              background:'rgba(220,53,69,0.82)', border:'none', color:'#fff',
              cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center',
            }}>
              <X size={14}/>
            </button>
          </div>
        ) : (
          <label style={{ ...uploadBtnStyle, width:'100%', boxSizing:'border-box', justifyContent:'center' }}>
            <ImageIcon size={15}/> Upload main image
            <input ref={mainInputRef} type="file" accept="image/*" style={{ display:'none' }} onChange={onMainFileChange}/>
          </label>
        )}
      </div>

      {/* ══ ADDITIONAL IMAGES ═══════════════════════════════════════════════════ */}
      <div style={{ marginBottom:20 }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
          <div style={{ display:'flex', alignItems:'center', gap:6 }}>
            <ImageIcon size={14} color="var(--brand-orange,#e85d04)"/>
            <span style={{ fontSize:'0.78rem', fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', color:'var(--text-muted)' }}>
              Additional Images
              {((form.images ?? []).length + extraFiles.length) > 0 && (
                <span style={{ marginLeft:6, color:'var(--brand-orange,#e85d04)' }}>
                  ({(form.images ?? []).length + extraFiles.length})
                </span>
              )}
            </span>
          </div>
          <label style={{ ...uploadBtnStyle, padding:'5px 12px', cursor:'pointer' }}>
            <Plus size={13}/> Add images
            <input ref={extraInputRef} type="file" accept="image/*" multiple style={{ display:'none' }} onChange={onExtraFilesChange}/>
          </label>
        </div>

        {/* Saved images */}
        {(form.images ?? []).length > 0 && (
          <>
            <p style={{ fontSize:'0.74rem', color:'var(--text-muted)', marginBottom:6, fontStyle:'italic' }}>
              Saved — click a caption to edit it
            </p>
            <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom: extraFiles.length ? 12 : 0 }}>
              {(form.images ?? []).map((img, i) => (
                <div key={`saved-${i}`} style={{
                  display:'flex', gap:10, alignItems:'center',
                  background:'var(--surface-alt,#f8f8f8)', borderRadius:8,
                  padding:8, border:'1px solid var(--border)',
                }}>
                  {/* Thumbnail */}
                  <div style={{ width:64, height:52, borderRadius:6, overflow:'hidden', flexShrink:0, background:'#e0e0e0' }}>
                    <img
                      src={img.url}
                      alt={img.caption || `Image ${i + 1}`}
                      style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }}
                      onError={e => { (e.target as HTMLImageElement).style.opacity = '0'; }}
                    />
                  </div>
                  {/* Editable caption */}
                  <input
                    className="form-input"
                    style={{ flex:1, margin:0 }}
                    value={img.caption}
                    onChange={e => updatePersistedCaption(i, e.target.value)}
                    placeholder="Caption (optional)"
                  />
                  {/* Saved badge */}
                  <span style={{
                    fontSize:'0.68rem', fontWeight:700, color:'#2d6a4f',
                    background:'rgba(45,106,79,0.1)', border:'1px solid rgba(45,106,79,0.2)',
                    borderRadius:6, padding:'2px 7px', flexShrink:0, whiteSpace:'nowrap',
                  }}>
                    saved
                  </span>
                  <button onClick={() => removePersistedImage(i)} style={{
                    width:28, height:28, borderRadius:6, flexShrink:0,
                    background:'rgba(220,53,69,0.08)', border:'1px solid rgba(220,53,69,0.2)',
                    color:'#dc3545', cursor:'pointer',
                    display:'flex', alignItems:'center', justifyContent:'center',
                  }}>
                    <Trash2 size={13}/>
                  </button>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Pending new images */}
        {extraFiles.length > 0 && (
          <>
            <p style={{ fontSize:'0.74rem', color:'var(--brand-orange,#e85d04)', marginBottom:6, fontStyle:'italic', fontWeight:600 }}>
              ● Pending — will upload on save
            </p>
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              {extraFiles.map((ef, i) => (
                <div key={`new-${i}`} style={{
                  display:'flex', gap:10, alignItems:'center',
                  background:'rgba(232,93,4,0.04)', borderRadius:8,
                  padding:8, border:'1px dashed rgba(232,93,4,0.35)',
                }}>
                  <div style={{ width:64, height:52, borderRadius:6, overflow:'hidden', flexShrink:0 }}>
                    <img src={ef.preview} alt="" style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }}/>
                  </div>
                  <input
                    className="form-input"
                    style={{ flex:1, margin:0 }}
                    value={ef.caption}
                    onChange={e => updateExtraCaption(i, e.target.value)}
                    placeholder="Caption (optional)"
                  />
                  <button onClick={() => removeExtra(i)} style={{
                    width:28, height:28, borderRadius:6, flexShrink:0,
                    background:'rgba(220,53,69,0.08)', border:'1px solid rgba(220,53,69,0.2)',
                    color:'#dc3545', cursor:'pointer',
                    display:'flex', alignItems:'center', justifyContent:'center',
                  }}>
                    <Trash2 size={13}/>
                  </button>
                </div>
              ))}
            </div>
          </>
        )}

        {(form.images ?? []).length === 0 && extraFiles.length === 0 && (
          <p style={{ fontSize:'0.8rem', color:'var(--text-muted)', fontStyle:'italic', margin:'4px 0 0' }}>
            No additional images yet. Use the button above to add some.
          </p>
        )}
      </div>

      <div style={{ borderTop:'1px solid var(--border)', margin:'4px 0 20px' }}/>

      {/* ══ PROJECT NOTES ════════════════════════════════════════════════════════ */}
      <div style={{ marginBottom:20 }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
          <div style={{ display:'flex', alignItems:'center', gap:6 }}>
            <StickyNote size={14} color="var(--brand-orange,#e85d04)"/>
            <span style={{ fontSize:'0.78rem', fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', color:'var(--text-muted)' }}>
              Project Notes
              {(form.notes ?? []).filter(n => n.trim()).length > 0 && (
                <span style={{ marginLeft:6, color:'var(--brand-orange,#e85d04)' }}>
                  ({(form.notes ?? []).filter(n => n.trim()).length})
                </span>
              )}
            </span>
          </div>
          <button onClick={addNote} style={{
            ...uploadBtnStyle,
            border:'1.5px dashed var(--brand-orange,#e85d04)',
            color:'var(--brand-orange,#e85d04)',
            padding:'5px 12px',
          }}>
            <Plus size={13}/> Add note
          </button>
        </div>

        {(form.notes ?? []).length === 0 ? (
          <p style={{ fontSize:'0.8rem', color:'var(--text-muted)', fontStyle:'italic' }}>
            No notes yet. Click "Add note" to attach project highlights or key facts.
          </p>
        ) : (
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            {(form.notes ?? []).map((note, i) => (
              <div key={i} style={{ display:'flex', gap:8, alignItems:'flex-start' }}>
                <span style={{
                  width:22, height:22, borderRadius:'50%',
                  background:'var(--brand-orange,#e85d04)', color:'#fff',
                  fontSize:'0.7rem', fontWeight:700, flexShrink:0, marginTop:9,
                  display:'flex', alignItems:'center', justifyContent:'center',
                }}>
                  {i + 1}
                </span>
                <textarea
                  className="form-textarea"
                  style={{ flex:1, minHeight:56, margin:0 }}
                  value={note}
                  onChange={e => updateNote(i, e.target.value)}
                  placeholder={`Note ${i + 1}…`}
                />
                <button onClick={() => removeNote(i)} style={{
                  width:28, height:28, borderRadius:6, marginTop:6, flexShrink:0,
                  background:'rgba(220,53,69,0.08)', border:'1px solid rgba(220,53,69,0.2)',
                  color:'#dc3545', cursor:'pointer',
                  display:'flex', alignItems:'center', justifyContent:'center',
                }}>
                  <Trash2 size={13}/>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Footer ── */}
      <div style={{ display:'flex', gap:12, justifyContent:'flex-end', paddingTop:8, borderTop:'1px solid var(--border)' }}>
        <button className="btn-outline" onClick={onClose}>Cancel</button>
        <button className="btn-primary" onClick={handleSave}>
          {mode === 'add' ? 'Add Building' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
};

// ── TeamForm ───────────────────────────────────────────────────────────────────
const TeamForm: React.FC<{ data:Member; mode:string; onSave:(d:Member)=>void; onClose:()=>void }> = ({ data, mode, onSave, onClose }) => {
  const [form, setForm] = useState<Member>(data);
  const ch = (e: any) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  return (
    <div className="contact-form">
      <div className="form-row">
        <div className="form-group"><label className="form-label">Full Name *</label><input className="form-input" name="name" value={form.name} onChange={ch} placeholder="Full name"/></div>
        <div className="form-group"><label className="form-label">Initials</label><input className="form-input" name="initials" value={form.initials} onChange={ch} placeholder="CEO" maxLength={4}/></div>
      </div>
      <div className="form-group"><label className="form-label">Role / Title *</label><input className="form-input" name="role" value={form.role} onChange={ch} placeholder="e.g. Chief Executive Officer"/></div>
      <div className="form-group"><label className="form-label">Bio</label><textarea className="form-textarea" name="bio" value={form.bio} onChange={ch} placeholder="Brief biography..."/></div>
      <div style={{ display:'flex', gap:12, justifyContent:'flex-end', marginTop:8 }}>
        <button className="btn-outline" onClick={onClose}>Cancel</button>
        <button className="btn-primary" onClick={() => onSave(form)}>{mode==='add' ? 'Add Member' : 'Save Changes'}</button>
      </div>
    </div>
  );
};

// ── ContactForm ────────────────────────────────────────────────────────────────
const ContactForm: React.FC<{ data:Contact; onSave:(d:Contact)=>void; onClose:()=>void; saving:boolean }> = ({ data, onSave, onClose, saving }) => {
  const [form, setForm] = useState<Contact>(data);
  const ch = (e: React.ChangeEvent<HTMLInputElement>) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  const fields: { name: keyof Contact; label: string; type?: string; placeholder: string; icon: React.ReactNode }[] = [
    { name:'phone',   label:'Phone Number',   type:'tel',   placeholder:'+234 800 NIDAVE',            icon:<Phone size={15}/> },
    { name:'email',   label:'Official Email', type:'email', placeholder:'info@nidave.com',             icon:<Mail size={15}/> },
    { name:'agency',  label:'Agency Email',   type:'email', placeholder:'sublet@nidave.com',           icon:<Mail size={15}/> },
    { name:'address', label:'Address',        type:'text',  placeholder:"La' Goshen Building, Ibadan", icon:<MapPin size={15}/> },
    { name:'hours',   label:'Working Hours',  type:'text',  placeholder:'Mon - Fri: 8am - 5pm',        icon:<Clock size={15}/> },
  ];
  return (
    <div className="contact-form">
      {fields.map(f => (
        <div className="form-group" key={f.name}>
          <label className="form-label" style={{ display:'flex', alignItems:'center', gap:6 }}>{f.icon} {f.label}</label>
          <input className="form-input" name={f.name} type={f.type||'text'} value={form[f.name]||''} onChange={ch} placeholder={f.placeholder}/>
        </div>
      ))}
      <div style={{ display:'flex', gap:12, justifyContent:'flex-end', marginTop:8 }}>
        <button className="btn-outline" onClick={onClose} disabled={saving}>Cancel</button>
        <button className="btn-primary" onClick={() => onSave(form)} disabled={saving}>{saving ? 'Saving…' : 'Save Changes'}</button>
      </div>
    </div>
  );
};

export default AdminDashboard;